import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../../config/api';
import { MapPin, Camera, Check, ChevronRight, LogOut, Loader2, Play, Upload, GripVertical, Clock, CheckCircle2, Image } from 'lucide-react';

const WorkerPortal = ({ worker, token, onLogout }) => {
  const [view, setView] = useState('loading'); // loading, plan, active, uploading
  const [sites, setSites] = useState([]);
  const [dayPlan, setDayPlan] = useState(null);
  const [activeVisit, setActiveVisit] = useState(null);
  const [selectedSites, setSelectedSites] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [photoType, setPhotoType] = useState('before');
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState('');
  const [completedSiteIds, setCompletedSiteIds] = useState([]);
  const fileInputRef = useRef(null);
  const gpsIntervalRef = useRef(null);
  const watchIdRef = useRef(null);
  const lastSendRef = useRef(0);
  const [gpsStatus, setGpsStatus] = useState('connecting'); // connecting, active, denied, unavailable

  const headers = { 'Authorization': `Bearer ${token}` };

  // Start GPS tracking - uses watchPosition for background tracking on mobile
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsStatus('unavailable');
      return;
    }

    const sendGPS = (lat, lng) => {
      const now = Date.now();
      // Throttle: only send every 2 minutes
      if (now - lastSendRef.current < 115000) return;
      lastSendRef.current = now;
      fetch(`${API_URL}/api/worker/gps`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude: lat, longitude: lng })
      }).catch(() => {});
    };

    // watchPosition keeps running in background on mobile (unlike getCurrentPosition + setInterval)
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setGpsStatus('active');
        sendGPS(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => {
        if (err.code === 1) setGpsStatus('denied');
        else setGpsStatus('unavailable');
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 60000 }
    );

    // Fallback interval for browsers that pause watchPosition when minimized
    gpsIntervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => sendGPS(pos.coords.latitude, pos.coords.longitude),
        () => {},
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
      );
    }, 120000);

    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      clearInterval(gpsIntervalRef.current);
    };
  }, [token]);

  // Load initial state
  useEffect(() => { loadState(); }, []);

  const loadState = async () => {
    try {
      // Check for active block
      const activeRes = await fetch(`${API_URL}/api/worker/block/active`, { headers });
      const activeData = await activeRes.json();
      if (activeData.visit) {
        setActiveVisit(activeData.visit);
        setPhotos(activeData.visit.photos || []);
        setView('active');
        return;
      }
      // Check for day plan
      const planRes = await fetch(`${API_URL}/api/worker/day-plan`, { headers });
      const planData = await planRes.json();
      if (planData.day_plan) {
        const plan = planData.day_plan;
        if (planData.source) plan.source = planData.source;
        setDayPlan(plan);
        // Load completed visits for today
        const todayRes = await fetch(`${API_URL}/api/worker/today-visits`, { headers });
        const todayData = await todayRes.json();
        setCompletedSiteIds(todayData.completed_site_ids || []);
        setView('planned');
      } else {
        // Load sites for planning
        const sitesRes = await fetch(`${API_URL}/api/worker/sites`, { headers });
        const sitesData = await sitesRes.json();
        setSites(sitesData.sites || []);
        setView('plan');
      }
    } catch {
      setError('Failed to load. Please refresh.');
      setView('plan');
    }
  };

  // Toggle site selection for day plan
  const toggleSite = (site) => {
    setSelectedSites(prev => {
      const exists = prev.find(s => s.id === site.id);
      if (exists) return prev.filter(s => s.id !== site.id);
      return [...prev, site];
    });
  };

  // Move site up in order
  const moveSite = (index, direction) => {
    const newList = [...selectedSites];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newList.length) return;
    [newList[index], newList[swapIdx]] = [newList[swapIdx], newList[index]];
    setSelectedSites(newList);
  };

  // Submit day plan
  const submitPlan = async () => {
    if (selectedSites.length === 0) { setError('Select at least one block'); return; }
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/worker/day-plan`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_ids: selectedSites.map(s => s.id) })
      });
      const data = await res.json();
      if (data.success) {
        setDayPlan(data.day_plan);
        setView('planned');
      }
    } catch { setError('Failed to save plan'); }
  };

  // Start a block
  const startBlock = async (siteId) => {
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/worker/block/start`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: siteId })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActiveVisit(data.visit);
        setPhotos([]);
        setPhotoType('before');
        setView('active');
      } else {
        setError(data.detail || 'Failed to start block');
      }
    } catch { setError('Connection error'); }
  };

  // Upload photo
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !activeVisit) return;
    setUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('visit_id', activeVisit.id);
        formData.append('photo_type', photoType);
        const res = await fetch(`${API_URL}/api/worker/block/upload-photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const data = await res.json();
        if (data.success) {
          setPhotos(prev => [...prev, data.photo]);
        }
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Finish block
  const finishBlock = async () => {
    if (!activeVisit) return;
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/worker/block/finish`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: activeVisit.site_id })
      });
      const data = await res.json();
      if (data.success) {
        setCompletedSiteIds(prev => [...prev, activeVisit.site_id]);
        setActiveVisit(null);
        setPhotos([]);
        setView('planned');
        // Reload day plan
        const planRes = await fetch(`${API_URL}/api/worker/day-plan`, { headers });
        const planData = await planRes.json();
        if (planData.day_plan) setDayPlan(planData.day_plan);
      } else {
        setError(data.detail || 'Failed to finish block');
      }
    } catch { setError('Connection error'); }
  };

  const handleLogout = () => {
    clearInterval(gpsIntervalRef.current);
    localStorage.removeItem('worker_token');
    localStorage.removeItem('worker_info');
    onLogout();
  };

  const beforePhotos = photos.filter(p => p.photo_type === 'before');
  const afterPhotos = photos.filter(p => p.photo_type === 'after');

  // ==================== RENDER ====================

  if (view === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a1f4e]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a1f4e] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div>
          <p className="font-semibold">{worker.name}</p>
          <div className="flex items-center gap-2">
            <p className="text-xs text-white/60">Just Cleaned Worker Portal</p>
            <span data-testid="gps-status-indicator" className={`text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1 ${
              gpsStatus === 'active' ? 'bg-green-500/20 text-green-300' :
              gpsStatus === 'denied' ? 'bg-red-500/20 text-red-300' :
              gpsStatus === 'unavailable' ? 'bg-red-500/20 text-red-300' :
              'bg-yellow-500/20 text-yellow-300'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${
                gpsStatus === 'active' ? 'bg-green-400 animate-pulse' :
                gpsStatus === 'denied' ? 'bg-red-400' :
                'bg-yellow-400 animate-pulse'
              }`} />
              {gpsStatus === 'active' ? 'GPS' : gpsStatus === 'denied' ? 'GPS Off' : gpsStatus === 'unavailable' ? 'No GPS' : 'GPS...'}
            </span>
          </div>
        </div>
        <button onClick={handleLogout} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="worker-logout-btn">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">{error}</div>
      )}

      {gpsStatus === 'denied' && (
        <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-700 text-sm">
          <p className="font-medium">Location tracking is off</p>
          <p className="text-xs mt-1">Enable location access in your browser settings so your manager can track your route.</p>
        </div>
      )}

      {/* DAY PLANNING VIEW */}
      {view === 'plan' && (
        <div className="p-4 space-y-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Plan Your Day</h2>
            <p className="text-gray-500 text-sm">Select blocks and set your order</p>
          </div>

          {/* Selected order */}
          {selectedSites.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <p className="text-sm font-medium text-gray-500 mb-3">Your order today:</p>
              {selectedSites.map((site, idx) => (
                <div key={site.id} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                  <span className="w-7 h-7 bg-[#1a1f4e] text-white rounded-full flex items-center justify-center text-sm font-bold">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{site.site_name}</p>
                    <p className="text-xs text-gray-500 truncate">{site.client_name}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => moveSite(idx, -1)} disabled={idx === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">▲</button>
                    <button onClick={() => moveSite(idx, 1)} disabled={idx === selectedSites.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">▼</button>
                    <button onClick={() => toggleSite(site)} className="p-1 text-red-400 hover:text-red-600 text-sm">✕</button>
                  </div>
                </div>
              ))}
              <button
                onClick={submitPlan}
                className="w-full mt-4 h-12 bg-[#00c853] hover:bg-[#00a844] text-white rounded-xl font-medium flex items-center justify-center gap-2"
                data-testid="submit-plan-btn"
              >
                <Check className="w-5 h-5" />
                Confirm Plan ({selectedSites.length} blocks)
              </button>
            </div>
          )}

          {/* Site list */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-500">All blocks:</p>
            {sites.map((site) => {
              const isSelected = selectedSites.some(s => s.id === site.id);
              return (
                <button
                  key={site.id}
                  onClick={() => toggleSite(site)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                    isSelected ? 'border-[#1a1f4e] bg-[#1a1f4e]/5' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  data-testid={`site-select-${site.id}`}
                >
                  <div className="flex items-center gap-3">
                    <MapPin className={`w-5 h-5 ${isSelected ? 'text-[#1a1f4e]' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{site.site_name}</p>
                      <p className="text-sm text-gray-500 truncate">{site.client_name} &middot; {site.site_address}</p>
                    </div>
                    {isSelected && <CheckCircle2 className="w-5 h-5 text-[#1a1f4e]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* PLANNED VIEW - Ready to start blocks */}
      {view === 'planned' && dayPlan && (
        <div className="p-4 space-y-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Today's Blocks</h2>
            <div className="flex items-center gap-2">
              <p className="text-gray-500 text-sm">{dayPlan.date}</p>
              {dayPlan.source === 'admin_schedule' && (
                <span data-testid="assigned-by-admin-badge" className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Assigned by admin</span>
              )}
            </div>
          </div>
          <div className="space-y-3">
            {dayPlan.sites.map((site, idx) => {
                const isDone = completedSiteIds.includes(site.site_id);
                return (
                <div key={site.site_id} className={`bg-white rounded-2xl border p-4 ${isDone ? 'border-green-200 bg-green-50/50' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${isDone ? 'bg-green-500 text-white' : 'bg-[#1a1f4e] text-white'}`}>
                      {isDone ? <CheckCircle2 className="w-5 h-5" /> : idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className={`font-semibold ${isDone ? 'text-green-800' : 'text-gray-900'}`}>{site.site_name}</p>
                      <p className="text-sm text-gray-500">{site.client_name} &middot; {site.site_address}</p>
                    </div>
                    {isDone && <span className="text-xs text-green-600 font-medium">Done</span>}
                  </div>
                  {isDone ? (
                    <div className="w-full h-11 bg-green-100 text-green-700 rounded-xl font-medium flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Completed
                    </div>
                  ) : (
                    <button
                      onClick={() => startBlock(site.site_id)}
                      className="w-full h-11 bg-[#00c853] hover:bg-[#00a844] text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                      data-testid={`start-block-${site.site_id}`}
                    >
                      <Play className="w-4 h-4" />
                      Start Block
                    </button>
                  )}
                </div>
                );
            })}
          </div>
          <button
            onClick={() => { setView('plan'); setSelectedSites([]); }}
            className="w-full h-10 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            data-testid="change-plan-btn"
          >
            Change Plan
          </button>
        </div>
      )}

      {/* ACTIVE BLOCK VIEW */}
      {view === 'active' && activeVisit && (
        <div className="p-4 space-y-4">
          {/* Block info */}
          <div className="bg-[#00c853]/10 rounded-2xl border border-[#00c853]/30 p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 bg-[#00c853] rounded-full animate-pulse" />
              <span className="text-sm font-medium text-[#00c853]">In Progress</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">{activeVisit.site_name}</h2>
            <p className="text-sm text-gray-500">{activeVisit.client_name} &middot; {activeVisit.site_address}</p>
          </div>

          {/* Photo type selector */}
          <div className="flex gap-2">
            <button
              onClick={() => setPhotoType('before')}
              className={`flex-1 h-11 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                photoType === 'before' ? 'bg-[#1a1f4e] text-white' : 'bg-white border border-gray-200 text-gray-700'
              }`}
              data-testid="photo-type-before"
            >
              <Camera className="w-4 h-4" />
              Before ({beforePhotos.length})
            </button>
            <button
              onClick={() => setPhotoType('after')}
              className={`flex-1 h-11 rounded-xl font-medium text-sm flex items-center justify-center gap-2 transition-all ${
                photoType === 'after' ? 'bg-[#1a1f4e] text-white' : 'bg-white border border-gray-200 text-gray-700'
              }`}
              data-testid="photo-type-after"
            >
              <Camera className="w-4 h-4" />
              After ({afterPhotos.length})
            </button>
          </div>

          {/* Upload button */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={handlePhotoUpload}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full h-14 bg-white border-2 border-dashed border-gray-300 hover:border-[#1a1f4e] rounded-xl flex items-center justify-center gap-3 text-gray-600 hover:text-[#1a1f4e] transition-all"
            data-testid="upload-photo-btn"
          >
            {uploading ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="w-5 h-5" /> Take / Upload {photoType} Photos</>
            )}
          </button>

          {/* Photo thumbnails */}
          {(photoType === 'before' ? beforePhotos : afterPhotos).length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {(photoType === 'before' ? beforePhotos : afterPhotos).map((photo) => (
                <div key={photo.id} className="aspect-square bg-gray-100 rounded-xl overflow-hidden relative">
                  <img
                    src={`${API_URL}/api/worker/photos/${photo.storage_path}?auth=${token}`}
                    alt={photo.photo_type}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className={`absolute top-1 left-1 px-2 py-0.5 rounded text-xs font-medium text-white ${
                    photo.photo_type === 'before' ? 'bg-amber-500' : 'bg-green-500'
                  }`}>
                    {photo.photo_type}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Finish block button */}
          <button
            onClick={finishBlock}
            className="w-full h-14 bg-[#1a1f4e] hover:bg-[#2a2f5e] text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors mt-4"
            data-testid="finish-block-btn"
          >
            <CheckCircle2 className="w-5 h-5" />
            Finished — Move to Next Block
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkerPortal;
